import { useCallback, useState } from 'react';
import {
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  Selection,
  Stack,
} from '@fluentui/react';
import moment from 'moment';
import { DetailsListDefault } from '../../../components/parts';
import { BillitInvoice } from '../../billit-invoices/types';
import { billitColors } from '../../billit-invoices/billitDetailUi';

interface Props {
  items: BillitInvoice[];
  enableShimmer?: boolean;
  onSelect?: (invoice: BillitInvoice) => void;
  loadMore?: boolean;
  loadMoreCallback?: () => void;
}

const buildColumns = (): IColumn[] => {
  const parseJsonField = (jsonString: string | null) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  };

  const getAssigneeName = (invoice: BillitInvoice) => {
    const emp = invoice.assigned_employee;
    if (!emp) return '-';
    return [emp.first_name, emp.last_name].filter(Boolean).join(' ') || '-';
  };

  const getSupplierName = (invoice: BillitInvoice) => {
    const supplier = parseJsonField(invoice.supplier_json);
    if (supplier?.Name) return supplier.Name;
    return '-';
  };

  const hasManagementComment = (invoice: BillitInvoice) => {
    const logs = invoice.billit_invoice_logs || [];
    if (logs.length === 0) return false;
    const sortedLogs = [...logs].sort(
      (a, b) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime(),
    );
    return sortedLogs[0].is_from_management === true;
  };

  return [
    {
      key: 'column1',
      name: 'Factuurnummer',
      fieldName: 'order_number',
      minWidth: 110,
      maxWidth: 140,
      isResizable: true,
      isRowHeader: true,
      onRender: (invoice: BillitInvoice) => (
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign='center'>
          <span>{invoice.order_number || '-'}</span>
          {hasManagementComment(invoice) && (
            <span
              style={{
                backgroundColor: billitColors.orange,
                color: 'white',
                borderRadius: '50%',
                width: '8px',
                height: '8px',
                display: 'inline-block',
              }}
              title='Administratie heeft opmerkingen toegevoegd'
            />
          )}
        </Stack>
      ),
      data: 'string',
      isPadded: true,
    },
    {
      key: 'column2',
      name: 'Datum',
      fieldName: 'order_date',
      minWidth: 90,
      maxWidth: 100,
      isResizable: true,
      isRowHeader: true,
      onRender: (invoice: BillitInvoice) => (
        <span>
          {invoice.order_date
            ? moment(invoice.order_date).format('DD/MM/YYYY')
            : '-'}
        </span>
      ),
      data: 'string',
      isPadded: true,
    },
    {
      key: 'columnAssignee',
      name: 'Toegewezen aan',
      fieldName: 'assigned_employee',
      minWidth: 160,
      maxWidth: 220,
      isResizable: true,
      isRowHeader: true,
      onRender: (inv: BillitInvoice) => <span>{getAssigneeName(inv)}</span>,
      data: 'string',
      isPadded: true,
    },
    {
      key: 'column3',
      name: 'Leverancier',
      fieldName: 'supplier',
      minWidth: 220,
      maxWidth: 320,
      isResizable: true,
      isRowHeader: true,
      onRender: (invoice: BillitInvoice) => <span>{getSupplierName(invoice)}</span>,
      data: 'string',
      isPadded: true,
    },
    {
      key: 'column4',
      name: 'Totaal (incl. BTW)',
      fieldName: 'total_incl',
      minWidth: 130,
      maxWidth: 150,
      isResizable: true,
      styles: { cellTitle: { justifyContent: 'flex-end' } },
      isRowHeader: true,
      onRender: (invoice: BillitInvoice) => (
        // bedragen rechts uitgelijnd, zodat de komma's onder elkaar staan
        <span style={{ display: 'block', textAlign: 'right' }}>
          {invoice.total_incl
            ? `${Number(invoice.total_incl).toFixed(2)} ${invoice.currency || 'EUR'}`
            : '-'}
        </span>
      ),
      data: 'string',
      isPadded: false,
    },
  ];
};

const BillitInvoicesAssignedDetailList = ({
  items,
  enableShimmer = false,
  onSelect,
  loadMore = false,
  loadMoreCallback = () => {},
}: Props) => {
  const [columns, setColumns] = useState<IColumn[]>(buildColumns);

  const onColumnResize = useCallback((column?: IColumn, newWidth?: number) => {
    const colKey = column?.key;
    if (!colKey || newWidth == null) return;
    setColumns(prev =>
      prev.map(col => {
        if (col.key !== colKey) return col;
        return { ...col, maxWidth: Math.max(col.minWidth, newWidth) };
      }));
  }, []);

  const selection = new Selection({
    onSelectionChanged: () => {
      const selectedItem = selection.getSelection()[0] as BillitInvoice;
      if (selectedItem && onSelect) {
        onSelect(selectedItem);
      }
    },
  });

  return (
    <DetailsListDefault
      items={items}
      columns={columns}
      layoutMode={DetailsListLayoutMode.fixedColumns}
      selection={selection}
      selectionMode={SelectionMode.single}
      enableShimmer={enableShimmer}
      loadMore={loadMore}
      lazyLoading={loadMore}
      loadMoreCallback={loadMoreCallback}
      onColumnResize={onColumnResize}
    />
  );
};

export default BillitInvoicesAssignedDetailList;
